import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { scalePoint, scoreLabel, type PatientSummary } from '@fatigue/shared';
import { Sparkline } from './Sparkline';
import { SidebarToggle } from './SidebarToggle';
import { InvitePatient } from './InvitePatient';

interface Props {
  readonly collapsed: boolean;
  readonly onToggle: () => void;
  readonly patients: readonly PatientSummary[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly selectedPatientId: string | null;
  readonly onSelect: (patientId: string) => void;
  /** Rendered when the roster is empty for a reason other than "none match". */
  readonly emptyReason: string | null;
}

/**
 * The roster, in two widths.
 *
 * Expanded: name, reference code, time since last entry, a 14-point sparkline
 * and the last level, by name and as a pip in its colour.
 *
 * Collapsed: a rail of initials, still clickable. Collapsing is for reclaiming
 * chart width, not for leaving the room -- a clinician comparing two patients
 * should be able to switch between them without expanding the list again. The
 * rail keeps the last-score colour too, so the roster does not go blind.
 *
 * The component stays mounted across the transition, so search text and scroll
 * position survive being collapsed and reopened.
 */
export function PatientSidebar({
  collapsed,
  onToggle,
  patients,
  loading,
  error,
  selectedPatientId,
  onSelect,
  emptyReason,
}: Props): JSX.Element {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === '') return patients;
    return patients.filter(
      (p) => p.displayName.toLowerCase().includes(q) || p.referenceCode.toLowerCase().includes(q),
    );
  }, [patients, query]);

  // The rail always shows everyone: a filter you cannot see is a filter you
  // will forget you set.
  const shown = collapsed ? patients : filtered;

  return (
    <nav
      className={`sidebar${collapsed ? ' sidebar--rail' : ''}`}
      aria-label="Patients"
    >
      {!collapsed && (
        <>
          <div className="sidebar__head">
            <span className="sidebar__title">Caseload</span>
            <span className="sidebar__count">{patients.length}</span>
          </div>

          <input
            type="search"
            className="sidebar__search"
            placeholder="Search patients"
            aria-label="Search patients"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </>
      )}

      {loading && !collapsed && <p className="empty">Loading&hellip;</p>}
      {error !== null && !collapsed && <p className="empty">{error}</p>}
      {/* "Nothing here" and "nothing matches your search" are different
          answers to different questions, and an owner with no cohort
          assignment deserves the first one rather than being left to guess. */}
      {!loading && error === null && shown.length === 0 && !collapsed && (
        <p className="empty">{patients.length === 0 && emptyReason !== null ? emptyReason : 'No patients match.'}</p>
      )}

      <ul className="sidebar__list" id="patient-list">
        {shown.map((patient) => {
          const selected = patient.patientId === selectedPatientId;
          const last = patient.lastEntry;
          const colour = last == null ? 'transparent' : scalePoint(last.score).colour;

          if (collapsed) {
            return (
              <li key={patient.patientId}>
                <button
                  type="button"
                  className={`rail${selected ? ' rail--on' : ''}`}
                  aria-current={selected}
                  onClick={() => onSelect(patient.patientId)}
                  // The rail shows initials, so the full name has to come from
                  // the label or the row is unusable with a screen reader.
                  aria-label={
                    last == null
                      ? `${patient.displayName}, no entries`
                      : `${patient.displayName}, last entry ${scoreLabel(last.score)}`
                  }
                  title={patient.displayName}
                >
                  <span className="rail__initials">{initialsOf(patient.displayName)}</span>
                  <span className="rail__bar" style={{ background: colour }} />
                </button>
              </li>
            );
          }

          return (
            <li key={patient.patientId}>
              <button
                type="button"
                className={`prow${selected ? ' prow--on' : ''}`}
                aria-current={selected}
                onClick={() => onSelect(patient.patientId)}
              >
                <span className="prow__main">
                  <span className="prow__name">{patient.displayName}</span>
                  <span className="prow__meta">
                    {patient.referenceCode} &middot; {sinceLabel(last?.capturedAt)}
                    {last != null && <> &middot; {scoreLabel(last.score)}</>}
                  </span>
                </span>

                <Sparkline scores={patient.recentScores} />

                {last != null && (
                  // Decorative: the level's name is in the meta line.
                  <span className="prow__pip" style={{ background: colour }} aria-hidden="true" />
                )}
              </button>
            </li>
          );
        })}
      </ul>

      {!collapsed && <InvitePatient />}

      {!collapsed && (
        <p className="sidebar__foot">
          No alerts are sent from this screen. Fatigue scores are reviewed in session.
        </p>
      )}

      <SidebarToggle collapsed={collapsed} onToggle={onToggle} />
    </nav>
  );
}

/** "Ellen Hartwood" -> "EH". Falls back to one letter for a single name. */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/** "4h ago" / "3d ago". Deliberately coarse -- precision would imply monitoring. */
function sinceLabel(capturedAt: string | undefined): string {
  if (capturedAt === undefined) return 'no entries';
  const hours = Math.round((Date.now() - new Date(capturedAt).getTime()) / 3_600_000);
  if (hours < 1) return 'just now';
  if (hours < 24) return `last ${hours}h ago`;
  return `last ${Math.round(hours / 24)}d ago`;
}
